/**
 * 标准配合全局数值求解内核（Phase 1 接入，Phase 3 解析雅可比）。
 *
 * 文件职责：
 * 1. 把参与求解的配合整体写成位姿变量上的残差方程组 C(q)=0，以当前位姿为初值做 Levenberg-Marquardt
 *    整体求解——覆盖"逐条投影不收敛且解析闭合规则库缺项"的任意拓扑（回环、跨对、松散中间件）；
 * 2. 作为图求解插槽里投影失败后的统一闭合兜底：只可能把原本报错的场景变成可解，
 *    不触碰任何原本成功的求解路径；产物必须通过整轮 validation 才返回，否则返回 null 落回旧行为；
 * 3. 变量以刚性块归并降维（每单元 6 参数 se(3) 增量、绕单元参考点旋转），锚定集 = 固定件 ∪ 驱动件 ∪
 *    预览锚点所在单元；块内配合与冗余共线同轴心不进残差；位姿更新走刚体流形步进（scale 不进计算）；
 * 4. 数值方法（Phase 3）：**on-manifold 重线性化**——每次迭代把已接受步长合入当前位姿、恒在 δ=0 处
 *    线性化，解析闭式雅可比（δp = δv + δω×(p−ref)、δd = δω×d 的一阶模型）替代有限差分；
 *    解析行构建导出给数值守卫测试与有限差分逐项对比；
 * 5. 收敛判据与校验判据同源（resolveStandardMateConstraintTolerance 的单一容差），方向残差按
 *    "线性/角度容差比"缩放到线性量纲，收敛即方向夹角 ≤ 角容差；
 * 6. 分支语义：瞬态配合方向按其 alignment 分支求解；已提交配合按初值半球连续（保持当前朝向）；
 *    signed/unsigned 的最终判定始终由整轮 validation 权威把关，内核只负责给出候选。
 *
 * 当前支持范围（超出即整体 bail、行为与内核不存在时完全一致）：
 * Concentric（轴-轴）、Coincident（面-面）、Parallel、Perpendicular；不支持 Distance/Angle/Tangent/Lock、
 * lockRotation、concentricMisalignment、referenceEntity 与非轴/面几何。按方案文档 Phase 3 逐步扩展。
 *
 * 开关：求解输入 `enableGlobalNumericKernel` 优先，其次全局键
 * `__PUPPYCAD_STANDARD_MATE_GLOBAL_NUMERIC_KERNEL__`（false 关闭），缺省开启——
 * 该候选只在"原本必然报错"的路径上运行且被 validation 兜底，缺省开启即规则库候选的同一风险包络。
 */

import * as THREE from 'three';
import {
  AssemblyMateType,
  MateAlignment,
  MateSolveStatus,
  type AssemblyMateDefinition,
} from '../definition';
import {
  createComponentWorldDeltaMap,
  transformMateDefinitionForSolve,
} from './geometry/StandardMateGeometryTransform';
import {
  resolveAxisGeometry,
  resolvePlaneGeometry,
} from './geometry/StandardMateGeometryAccess';
import {
  resolveStandardMateConstraintTolerance,
  type StandardMateConstraintTolerance,
} from './StandardMateConstraintResidual';
import { resolveStandardMateRigidComponentGroups } from './StandardMateRigidGroup';
import {
  createGraphSolverResult,
  transformComponentStateByWorldDelta,
} from './StandardMateCandidateResult';
import {
  calculateStandardMateMaxResidual,
  calculateStandardMateResidualCost,
  solveStandardMateDampedLeastSquaresStep,
} from './numeric/StandardMateLeastSquares';
import {
  appendStandardMateSolverDebugTraceEvent,
  type StandardMateSolverDebugTraceSession,
} from './StandardMateSolverDebugTrace';
import type {
  AssemblyMateSolverComponentState,
  StandardMateSolverInput,
  StandardMateSolverResult,
} from './StandardMateSolverTypes';

/** 浏览器或宿主环境中关闭全局数值内核的全局键（false 关闭；缺省开启）。 */
export const STANDARD_MATE_GLOBAL_NUMERIC_KERNEL_GLOBAL_KEY = '__PUPPYCAD_STANDARD_MATE_GLOBAL_NUMERIC_KERNEL__';

/** 变量单元数上限：超出说明作用域异常庞大，回落旧行为避免热路径超时。 */
const MAX_VARIABLE_UNIT_COUNT = 16;

/** 参与配合数上限。 */
const MAX_PARTICIPATING_MATE_COUNT = 64;

/** LM 最大迭代次数（与平面转动副求解器一致）。 */
const MAX_ITERATION_COUNT = 40;

/** 方向残差在完全反向（鞍点）处的脱困权重。 */
const SADDLE_ESCAPE_WEIGHT = 0.5;

/**
 * 单个变量单元：一个浮动组件或一个刚性块，共享 6 参数 se(3) 增量。
 *
 * 驱动部分自由度（Phase 3）：单元含被驱动组件且 drivenComponentConstraints 只保留部分位姿分量时，
 * 该单元成为**偏锚定变量**——被保留的分量参数钉死（雅可比列清零 + 步长分量置零），未保留的分量
 * 照常求解。preserveRotation 且不 preserveTranslation 即"旋转按用户输入保持、平移由机构联动决定"，
 * 正是驱动闭环（四连杆等）的语义。
 */
interface NumericKernelUnit {
  /** 单元 ID（组件 ID 或刚性组根 ID）。 */
  readonly unitId: string;
  /** 单元内全部组件实例 ID。 */
  readonly memberIds: readonly string[];
  /** 初值位姿下的旋转参考点（世界系）。 */
  readonly referencePoint: THREE.Vector3;
  /** 平移分量是否为自由变量。 */
  readonly freeTranslation: boolean;
  /** 旋转分量是否为自由变量。 */
  readonly freeRotation: boolean;
}

/**
 * 单元迭代运行时状态：累计世界 delta 与随动参考点（on-manifold 重线性化的载体）。
 */
interface NumericKernelUnitState {
  /** 相对初值位姿的累计世界 delta。 */
  accumulatedDelta: THREE.Matrix4;
  /** 当前位姿下的旋转参考点（随累计 delta 移动，保证增量恒绕单元当前中心）。 */
  referencePoint: THREE.Vector3;
}

/**
 * 单条参与残差的配合规格。
 */
interface NumericKernelMateSpec {
  /** 原始 Mate 定义。 */
  readonly mate: AssemblyMateDefinition;
  /** 两端所属单元 ID（同单元/双锚定的已在构建期剔除或判定不可解）。 */
  readonly unitIds: readonly string[];
  /** 方向分支符号（+1 同向 / −1 反向；Perpendicular 恒 +1，不参与方向对齐）。 */
  readonly directionSign: number;
  /**
   * 是否按动态无向处理方向残差（每次评估取当前半球符号）。
   *
   * 已提交圆孔同轴心的轴向符号来自 BRep 面朝向、不是用户约束（validation 按无向中心线校验），
   * 翻转重试把连通子图整翻 π 后其轴向必然反向——冻结初值半球会制造假鞍点，动态无向才与
   * validation 语义同源。瞬态方向配合仍按冻结分支求解（它们要落到明确的对齐分支）。
   */
  readonly dynamicUnsignedDirection: boolean;
  /** 该配合的残差行数。 */
  readonly residualRowCount: number;
}

/**
 * 解析雅可比构建所需的单侧实体运动学信息。
 */
interface KernelEntityKinematics {
  /** 所属变量单元序号；锚定侧为 null（雅可比列块为零）。 */
  readonly unitIndex: number | null;
  /** 该单元当前旋转参考点；锚定侧为 null。 */
  readonly referencePoint: THREE.Vector3 | null;
}

/**
 * 读取全局内核开关。
 *
 * @param input 求解器输入。
 * @returns 是否启用内核。
 */
function resolveKernelEnabled(input: StandardMateSolverInput): boolean {
  if (typeof input.enableGlobalNumericKernel === 'boolean') {
    return input.enableGlobalNumericKernel;
  }
  const raw = (globalThis as Record<string, unknown>)[STANDARD_MATE_GLOBAL_NUMERIC_KERNEL_GLOBAL_KEY];
  if (raw === false) {
    return false;
  }
  if (raw !== null && typeof raw === 'object' && (raw as { enabled?: unknown }).enabled === false) {
    return false;
  }
  return true;
}

/**
 * 判断组件是否固定。
 *
 * @param component 组件状态。
 * @returns 是否固定。
 */
function isFixedComponentState(component: AssemblyMateSolverComponentState | undefined): boolean {
  return component?.fixationState === 'fixed';
}

/**
 * 解析配合实体的方向向量（轴向或面法向）。
 *
 * @param mate 已变换到当前位姿的 Mate 定义。
 * @param side 实体侧。
 * @returns 单位方向；无轴/面几何时返回 null。
 */
function resolveEntityDirection(mate: AssemblyMateDefinition, side: 'first' | 'second'): THREE.Vector3 | null {
  const entity = side === 'first' ? mate.firstEntity : mate.secondEntity;
  const axis = resolveAxisGeometry(entity);
  if (axis) {
    return axis.direction.clone().normalize();
  }
  const plane = resolvePlaneGeometry(entity);
  if (plane) {
    return plane.normal.clone().normalize();
  }
  return null;
}

/**
 * 校验配合是否落在当前内核支持范围内。
 *
 * @param mate 原始 Mate 定义。
 * @param solvedMate 已变换到当前位姿的 Mate 定义。
 * @returns 是否支持。
 */
function isMateSupportedByKernel(mate: AssemblyMateDefinition, solvedMate: AssemblyMateDefinition): boolean {
  if (mate.suppressed || mate.referenceEntity || mate.lockRotation || mate.concentricMisalignment) {
    return false;
  }
  switch (mate.mateType) {
    case AssemblyMateType.Concentric:
      return resolveAxisGeometry(solvedMate.firstEntity) !== null
        && resolveAxisGeometry(solvedMate.secondEntity) !== null;
    case AssemblyMateType.Coincident:
      return resolvePlaneGeometry(solvedMate.firstEntity) !== null
        && resolvePlaneGeometry(solvedMate.secondEntity) !== null;
    case AssemblyMateType.Parallel:
    case AssemblyMateType.Perpendicular:
      return resolveEntityDirection(solvedMate, 'first') !== null
        && resolveEntityDirection(solvedMate, 'second') !== null;
    default:
      return false;
  }
}

/**
 * 计算单条配合的残差行数。
 *
 * @param mateType 配合类型。
 * @returns 残差行数。
 */
function resolveMateResidualRowCount(mateType: AssemblyMateType): number {
  switch (mateType) {
    case AssemblyMateType.Concentric:
      return 7; // 方向 cross(3) + 鞍点脱困(1) + 径向(3)
    case AssemblyMateType.Coincident:
      return 5; // 方向 cross(3) + 鞍点脱困(1) + 面距(1)
    case AssemblyMateType.Parallel:
      return 4; // 方向 cross(3) + 鞍点脱困(1)
    case AssemblyMateType.Perpendicular:
      return 1; // 点积(1)
    default:
      return 0;
  }
}

/**
 * 解析配合的首选方向分支符号：初值半球连续（保持当前朝向，等价"最小运动"语义）。
 *
 * 说明：瞬态配合同样按初值半球——瞬态初始放置已把草稿摆到目标分支附近，而存储的 alignment
 * 在无向校验场景下与几何符号可能无关（如圆孔同轴心旁的贴合面法向反平行却存 aligned）。
 * 若半球分支解不出，主流程会对瞬态方向配合翻转分支重试一次，覆盖"用户刚切换对齐、几何仍在
 * 旧分支"的场景；最终 signed/unsigned 判定始终由 validation 权威把关。
 * 初值近垂直无法判定半球时回退 alignment。
 *
 * @param mate 原始 Mate 定义。
 * @param baseSolvedMate 初值位姿下的 Mate 定义。
 * @returns 方向分支符号。
 */
function resolveDirectionSign(
  mate: AssemblyMateDefinition,
  baseSolvedMate: AssemblyMateDefinition,
): number {
  const alignmentSign = mate.alignment === MateAlignment.AntiAligned ? -1 : 1;
  if (mate.mateType === AssemblyMateType.Perpendicular) {
    return 1;
  }
  const firstDirection = resolveEntityDirection(baseSolvedMate, 'first');
  const secondDirection = resolveEntityDirection(baseSolvedMate, 'second');
  if (!firstDirection || !secondDirection) {
    return alignmentSign;
  }
  const dot = firstDirection.dot(secondDirection);
  return Math.abs(dot) < 1e-9 ? alignmentSign : Math.sign(dot);
}

/**
 * 解析该配合在当前位姿下的第二侧方向符号（动态无向取当前半球，冻结分支取 directionSign）。
 *
 * @param spec 配合规格。
 * @param firstDirection 第一侧单位方向。
 * @param rawSecondDirection 第二侧原始单位方向。
 * @returns 应用到第二侧的符号。
 */
function resolveCurrentSecondDirectionSign(
  spec: NumericKernelMateSpec,
  firstDirection: THREE.Vector3,
  rawSecondDirection: THREE.Vector3,
): number {
  return spec.dynamicUnsignedDirection
    ? (firstDirection.dot(rawSecondDirection) < 0 ? -1 : 1)
    : spec.directionSign;
}

/**
 * 向残差数组追加单条配合的残差分量。
 *
 * 导出仅供数值守卫测试与解析雅可比做有限差分逐项对比，业务代码不得直接调用。
 *
 * @param output 残差输出数组。
 * @param solvedMate 当前迭代位姿下的 Mate 定义。
 * @param spec 配合规格。
 * @param directionScale 方向残差 → 线性量纲缩放（linear/angular 容差比）。
 */
export function appendStandardMateKernelResiduals(
  output: number[],
  solvedMate: AssemblyMateDefinition,
  spec: NumericKernelMateSpec,
  directionScale: number,
): void {
  const sign = spec.directionSign;
  switch (spec.mate.mateType) {
    case AssemblyMateType.Concentric: {
      const firstAxis = resolveAxisGeometry(solvedMate.firstEntity)!;
      const secondAxis = resolveAxisGeometry(solvedMate.secondEntity)!;
      const firstDirection = firstAxis.direction.clone().normalize();
      const dynamicSign = resolveCurrentSecondDirectionSign(spec, firstDirection, secondAxis.direction);
      const secondDirection = secondAxis.direction.clone().normalize().multiplyScalar(dynamicSign);
      const cross = firstDirection.clone().cross(secondDirection);
      output.push(cross.x * directionScale, cross.y * directionScale, cross.z * directionScale);
      output.push((1 - firstDirection.dot(secondDirection)) * directionScale * SADDLE_ESCAPE_WEIGHT);
      // 径向：第二轴原点到第一轴线的垂直偏移（绕轴平移/旋转均不改变，正确保留圆柱副剩余自由度）。
      const offset = secondAxis.origin.clone().sub(firstAxis.origin);
      const radial = offset.clone().sub(firstDirection.clone().multiplyScalar(offset.dot(firstDirection)));
      output.push(radial.x, radial.y, radial.z);
      return;
    }
    case AssemblyMateType.Coincident: {
      const firstPlane = resolvePlaneGeometry(solvedMate.firstEntity)!;
      const secondPlane = resolvePlaneGeometry(solvedMate.secondEntity)!;
      const firstNormal = firstPlane.normal.clone().normalize();
      const secondNormal = secondPlane.normal.clone().normalize().multiplyScalar(sign);
      const cross = firstNormal.clone().cross(secondNormal);
      output.push(cross.x * directionScale, cross.y * directionScale, cross.z * directionScale);
      output.push((1 - firstNormal.dot(secondNormal)) * directionScale * SADDLE_ESCAPE_WEIGHT);
      output.push(secondPlane.point.clone().sub(firstPlane.point).dot(firstNormal));
      return;
    }
    case AssemblyMateType.Parallel: {
      const firstDirection = resolveEntityDirection(solvedMate, 'first')!;
      const secondDirection = resolveEntityDirection(solvedMate, 'second')!.multiplyScalar(sign);
      const cross = firstDirection.clone().cross(secondDirection);
      output.push(cross.x * directionScale, cross.y * directionScale, cross.z * directionScale);
      output.push((1 - firstDirection.dot(secondDirection)) * directionScale * SADDLE_ESCAPE_WEIGHT);
      return;
    }
    case AssemblyMateType.Perpendicular: {
      const firstDirection = resolveEntityDirection(solvedMate, 'first')!;
      const secondDirection = resolveEntityDirection(solvedMate, 'second')!;
      output.push(firstDirection.dot(secondDirection) * directionScale);
      return;
    }
    default:
  }
}

/**
 * 计算基向量与向量的叉积三元组：`e_k × v`（k = x/y/z）。
 *
 * 一阶刚体运动学的核心构件：δd = δω × d、δp = δv + δω × (p − ref)，
 * 对第 k 个旋转参数的偏导即 `e_k × (·)`。
 *
 * @param v 向量。
 * @returns 三个基向量叉积。
 */
function crossBasis(v: THREE.Vector3): readonly [THREE.Vector3, THREE.Vector3, THREE.Vector3] {
  return [
    new THREE.Vector3(0, -v.z, v.y),
    new THREE.Vector3(v.z, 0, -v.x),
    new THREE.Vector3(-v.y, v.x, 0),
  ] as const;
}

/**
 * 向雅可比行写入某单元列块的方向类偏导（cross 3 行 + 鞍点脱困 1 行）。
 *
 * @param jacobian 雅可比矩阵。
 * @param rowOffset 该配合行起点。
 * @param unitIndex 目标单元序号（锚定侧 null 直接跳过）。
 * @param ownDirection 该侧方向向量（已含符号）。
 * @param otherDirection 对侧方向向量（已含符号）。
 * @param isFirstSide 是否为 cross 的左操作数侧。
 * @param directionScale 方向缩放。
 */
function writeDirectionJacobianColumns(
  jacobian: number[][],
  rowOffset: number,
  unitIndex: number | null,
  ownDirection: THREE.Vector3,
  otherDirection: THREE.Vector3,
  isFirstSide: boolean,
  directionScale: number,
): void {
  if (unitIndex === null) {
    return;
  }
  const columnBase = unitIndex * 6 + 3;
  crossBasis(ownDirection).forEach((delta, k) => {
    // cross = d1 × d2：左侧扰动贡献 (e_k×d1)×d2，右侧扰动贡献 d1×(e_k×d2)。
    const crossDelta = isFirstSide ? delta.clone().cross(otherDirection) : otherDirection.clone().cross(delta);
    jacobian[rowOffset]![columnBase + k] += crossDelta.x * directionScale;
    jacobian[rowOffset + 1]![columnBase + k] += crossDelta.y * directionScale;
    jacobian[rowOffset + 2]![columnBase + k] += crossDelta.z * directionScale;
    // 鞍点脱困行 r = s·w·(1 − d1·d2)：∂r = −s·w·(∂d·d_other)。
    jacobian[rowOffset + 3]![columnBase + k] += -delta.dot(otherDirection) * directionScale * SADDLE_ESCAPE_WEIGHT;
  });
}

/**
 * 向解析雅可比追加单条配合的行块（on-manifold δ=0 处的闭式一阶模型）。
 *
 * 导出仅供数值守卫测试与有限差分逐项对比，业务代码不得直接调用。
 *
 * @param jacobian 雅可比矩阵（行数 = 全部残差行，列数 = 变量单元数 × 6）。
 * @param rowOffset 该配合的行起点。
 * @param solvedMate 当前位姿下的 Mate 定义。
 * @param spec 配合规格。
 * @param firstKinematics 第一侧运动学信息。
 * @param secondKinematics 第二侧运动学信息。
 * @param directionScale 方向残差缩放。
 */
export function appendStandardMateKernelJacobianRows(
  jacobian: number[][],
  rowOffset: number,
  solvedMate: AssemblyMateDefinition,
  spec: NumericKernelMateSpec,
  firstKinematics: KernelEntityKinematics,
  secondKinematics: KernelEntityKinematics,
  directionScale: number,
): void {
  switch (spec.mate.mateType) {
    case AssemblyMateType.Concentric: {
      const firstAxis = resolveAxisGeometry(solvedMate.firstEntity)!;
      const secondAxis = resolveAxisGeometry(solvedMate.secondEntity)!;
      const d1 = firstAxis.direction.clone().normalize();
      const dynamicSign = resolveCurrentSecondDirectionSign(spec, d1, secondAxis.direction);
      const d2 = secondAxis.direction.clone().normalize().multiplyScalar(dynamicSign);
      writeDirectionJacobianColumns(jacobian, rowOffset, firstKinematics.unitIndex, d1, d2, true, directionScale);
      writeDirectionJacobianColumns(jacobian, rowOffset, secondKinematics.unitIndex, d2, d1, false, directionScale);

      // 径向 r = P·o，P = I − d1·d1ᵀ，o = p2 − p1。
      const p1 = firstAxis.origin;
      const p2 = secondAxis.origin;
      const offset = p2.clone().sub(p1);
      const project = (v: THREE.Vector3): THREE.Vector3 => v.clone().sub(d1.clone().multiplyScalar(v.dot(d1)));
      const radialRow = rowOffset + 4;
      const writeRadialColumn = (column: number, value: THREE.Vector3): void => {
        jacobian[radialRow]![column] += value.x;
        jacobian[radialRow + 1]![column] += value.y;
        jacobian[radialRow + 2]![column] += value.z;
      };
      if (secondKinematics.unitIndex !== null) {
        const columnBase = secondKinematics.unitIndex * 6;
        const leverArm = p2.clone().sub(secondKinematics.referencePoint!);
        for (let k = 0; k < 3; k += 1) {
          const basis = new THREE.Vector3(k === 0 ? 1 : 0, k === 1 ? 1 : 0, k === 2 ? 1 : 0);
          writeRadialColumn(columnBase + k, project(basis));
        }
        crossBasis(leverArm).forEach((delta, k) => {
          writeRadialColumn(columnBase + 3 + k, project(delta));
        });
      }
      if (firstKinematics.unitIndex !== null) {
        const columnBase = firstKinematics.unitIndex * 6;
        const leverArm = p1.clone().sub(firstKinematics.referencePoint!);
        for (let k = 0; k < 3; k += 1) {
          const basis = new THREE.Vector3(k === 0 ? 1 : 0, k === 1 ? 1 : 0, k === 2 ? 1 : 0);
          writeRadialColumn(columnBase + k, project(basis).multiplyScalar(-1));
        }
        const armDeltas = crossBasis(leverArm);
        crossBasis(d1).forEach((directionDelta, k) => {
          // ∂r = P·m_k − c_k·(d1·o) − d1·(c_k·o)，其中 m_k = −e_k×(p1−ref)、c_k = e_k×d1。
          const pointDelta = armDeltas[k]!.clone().multiplyScalar(-1);
          const value = project(pointDelta)
            .sub(directionDelta.clone().multiplyScalar(d1.dot(offset)))
            .sub(d1.clone().multiplyScalar(directionDelta.dot(offset)));
          writeRadialColumn(columnBase + 3 + k, value);
        });
      }
      return;
    }
    case AssemblyMateType.Coincident: {
      const firstPlane = resolvePlaneGeometry(solvedMate.firstEntity)!;
      const secondPlane = resolvePlaneGeometry(solvedMate.secondEntity)!;
      const n1 = firstPlane.normal.clone().normalize();
      const n2 = secondPlane.normal.clone().normalize().multiplyScalar(spec.directionSign);
      writeDirectionJacobianColumns(jacobian, rowOffset, firstKinematics.unitIndex, n1, n2, true, directionScale);
      writeDirectionJacobianColumns(jacobian, rowOffset, secondKinematics.unitIndex, n2, n1, false, directionScale);

      // 面距 r = (p2 − p1)·n1。
      const distanceRow = rowOffset + 4;
      const offset = secondPlane.point.clone().sub(firstPlane.point);
      if (secondKinematics.unitIndex !== null) {
        const columnBase = secondKinematics.unitIndex * 6;
        jacobian[distanceRow]![columnBase] += n1.x;
        jacobian[distanceRow]![columnBase + 1] += n1.y;
        jacobian[distanceRow]![columnBase + 2] += n1.z;
        crossBasis(secondPlane.point.clone().sub(secondKinematics.referencePoint!)).forEach((delta, k) => {
          jacobian[distanceRow]![columnBase + 3 + k] += n1.dot(delta);
        });
      }
      if (firstKinematics.unitIndex !== null) {
        const columnBase = firstKinematics.unitIndex * 6;
        jacobian[distanceRow]![columnBase] += -n1.x;
        jacobian[distanceRow]![columnBase + 1] += -n1.y;
        jacobian[distanceRow]![columnBase + 2] += -n1.z;
        const armDeltas = crossBasis(firstPlane.point.clone().sub(firstKinematics.referencePoint!));
        crossBasis(n1).forEach((normalDelta, k) => {
          // ∂r = (e_k×n1)·o − n1·(e_k×(p1−ref))。
          jacobian[distanceRow]![columnBase + 3 + k] += normalDelta.dot(offset) - n1.dot(armDeltas[k]!);
        });
      }
      return;
    }
    case AssemblyMateType.Parallel: {
      const d1 = resolveEntityDirection(solvedMate, 'first')!;
      const d2 = resolveEntityDirection(solvedMate, 'second')!.multiplyScalar(spec.directionSign);
      writeDirectionJacobianColumns(jacobian, rowOffset, firstKinematics.unitIndex, d1, d2, true, directionScale);
      writeDirectionJacobianColumns(jacobian, rowOffset, secondKinematics.unitIndex, d2, d1, false, directionScale);
      return;
    }
    case AssemblyMateType.Perpendicular: {
      const d1 = resolveEntityDirection(solvedMate, 'first')!;
      const d2 = resolveEntityDirection(solvedMate, 'second')!;
      if (firstKinematics.unitIndex !== null) {
        const columnBase = firstKinematics.unitIndex * 6 + 3;
        crossBasis(d1).forEach((delta, k) => {
          jacobian[rowOffset]![columnBase + k] += delta.dot(d2) * directionScale;
        });
      }
      if (secondKinematics.unitIndex !== null) {
        const columnBase = secondKinematics.unitIndex * 6 + 3;
        crossBasis(d2).forEach((delta, k) => {
          jacobian[rowOffset]![columnBase + k] += d1.dot(delta) * directionScale;
        });
      }
      return;
    }
    default:
  }
}

/**
 * 由 6 参数增量构建单元世界 delta：先绕参考点旋转、再平移。
 *
 * @param parameters 全量参数数组。
 * @param parameterOffset 该单元的参数起点。
 * @param referencePoint 单元旋转参考点。
 * @returns 世界 delta 矩阵。
 */
function composeUnitWorldDelta(
  parameters: readonly number[],
  parameterOffset: number,
  referencePoint: THREE.Vector3,
): THREE.Matrix4 {
  const translation = new THREE.Vector3(
    parameters[parameterOffset] ?? 0,
    parameters[parameterOffset + 1] ?? 0,
    parameters[parameterOffset + 2] ?? 0,
  );
  const rotationVector = new THREE.Vector3(
    parameters[parameterOffset + 3] ?? 0,
    parameters[parameterOffset + 4] ?? 0,
    parameters[parameterOffset + 5] ?? 0,
  );
  const angle = rotationVector.length();
  const rotation = angle > 0
    ? new THREE.Matrix4().makeRotationAxis(rotationVector.clone().multiplyScalar(1 / angle), angle)
    : new THREE.Matrix4().identity();
  // M = T(v) ∘ T(ref) ∘ R(ω) ∘ T(−ref)：绕单元参考点旋转避免"绕世界原点旋转"带来的病态耦合。
  return new THREE.Matrix4()
    .makeTranslation(referencePoint.x + translation.x, referencePoint.y + translation.y, referencePoint.z + translation.z)
    .multiply(rotation)
    .multiply(new THREE.Matrix4().makeTranslation(-referencePoint.x, -referencePoint.y, -referencePoint.z));
}

/**
 * 把单个单元的世界 delta 应用到组件状态表（浅拷贝语义，仅替换该单元成员）。
 *
 * 说明：实体几何的当前位姿由组件状态（含选择位姿基线）唯一决定——deltaMap 在
 * transformMateEntityRef 中只是无选择位姿实体的 fallback，因此试算必须更新组件状态本体。
 *
 * @param target 目标组件状态表（就地替换成员）。
 * @param unit 变量单元。
 * @param delta 该单元世界 delta。
 * @param baseComponents 基态组件状态表（成员变换的基准）。
 */
function applyUnitDeltaToComponents(
  target: Record<string, AssemblyMateSolverComponentState>,
  unit: NumericKernelUnit,
  delta: THREE.Matrix4,
  baseComponents: Readonly<Record<string, AssemblyMateSolverComponentState>>,
): void {
  unit.memberIds.forEach((memberId) => {
    const baseComponent = baseComponents[memberId];
    if (baseComponent) {
      target[memberId] = transformComponentStateByWorldDelta(baseComponent, delta);
    }
  });
}

/**
 * 解析单元旋转参考点：优先世界位姿位置，缺省局部位姿位置。
 *
 * @param components 组件状态表。
 * @param memberIds 单元成员。
 * @returns 参考点。
 */
function resolveUnitReferencePoint(
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
  memberIds: readonly string[],
): THREE.Vector3 {
  const reference = new THREE.Vector3();
  let count = 0;
  memberIds.forEach((memberId) => {
    const component = components[memberId];
    if (!component) {
      return;
    }
    const position = component.worldTransform?.position ?? component.transform.position;
    reference.add(new THREE.Vector3(position[0], position[1], position[2]));
    count += 1;
  });
  return count > 0 ? reference.multiplyScalar(1 / count) : reference;
}

/**
 * 尝试用全局数值内核求解当前输入。
 *
 * @param input 求解器输入。
 * @param debugTrace 调试追踪会话。
 * @returns 通过整轮 validation 的求解结果；内核不适用/未收敛/校验未过时返回 null（回落旧行为）。
 */
export function trySolveStandardMateGlobalNumericKernel(
  input: StandardMateSolverInput,
  debugTrace?: StandardMateSolverDebugTraceSession | null,
): StandardMateSolverResult | null {
  if (!resolveKernelEnabled(input)) {
    return null;
  }
  const startedAt = performance.now();
  const participatingMates = input.mateDefinitions.filter((mate) => !mate.suppressed);
  if (participatingMates.length === 0 || participatingMates.length > MAX_PARTICIPATING_MATE_COUNT) {
    return null;
  }

  const tolerance = resolveStandardMateConstraintTolerance({
    epsilon: input.epsilon,
    angleEpsilon: input.angleEpsilon,
  });
  const directionScale = tolerance.linear / tolerance.angular;
  const transientMateIds = new Set(input.transientMateIds ?? []);
  const baseDeltaMap = createComponentWorldDeltaMap(input.components);
  const baseSolvedMates = new Map<string, AssemblyMateDefinition>();
  for (const mate of participatingMates) {
    const solvedMate = transformMateDefinitionForSolve(mate, baseDeltaMap, input.components);
    if (!isMateSupportedByKernel(mate, solvedMate)) {
      appendStandardMateSolverDebugTraceEvent(debugTrace, 'globalNumericKernel.bail', {
        reason: 'unsupported-mate',
        mateId: mate.mateId,
        mateType: mate.mateType,
      });
      return null;
    }
    baseSolvedMates.set(mate.mateId, solvedMate);
  }

  // 刚性块归并（只认已提交配合，与主求解器一致）；不排除固定/驱动块——它们整体成为锚定单元。
  const committedCurrentMates = participatingMates
    .filter((mate) => !transientMateIds.has(mate.mateId))
    .map((mate) => baseSolvedMates.get(mate.mateId)!);
  const rigidGroups = resolveStandardMateRigidComponentGroups(input, committedCurrentMates, tolerance);

  // 组件 → 单元归并。
  const memberIdsByUnitId = new Map<string, string[]>();
  Object.keys(input.components).forEach((componentId) => {
    const unitId = rigidGroups.groupRootByComponentId.get(componentId) ?? componentId;
    const members = memberIdsByUnitId.get(unitId) ?? [];
    members.push(componentId);
    memberIdsByUnitId.set(unitId, members);
  });

  // 锚定集：固定件 ∪ 预览锚点 ∪ 完全保留的驱动件所在单元；部分保留驱动件成为偏锚定变量单元；
  // 全浮动无锚时锚成员最多的单元防整体漂移。
  const previewAnchorIds = new Set<string>(input.previewAnchorComponentInstanceIds ?? []);
  const drivenIds = new Set<string>(input.drivenComponentInstanceIds ?? []);
  const anchoredUnitIds = new Set<string>();
  const partialFreedomByUnitId = new Map<string, { freeTranslation: boolean; freeRotation: boolean }>();
  memberIdsByUnitId.forEach((memberIds, unitId) => {
    const hardAnchored = memberIds.some((memberId) => (
      isFixedComponentState(input.components[memberId]) || previewAnchorIds.has(memberId)
    ));
    if (hardAnchored) {
      anchoredUnitIds.add(unitId);
      return;
    }
    // 驱动成员：按 drivenComponentConstraints 合并保留分量（任一成员保留即钉死该分量）。
    // 无显式约束的驱动成员保持历史语义：完整位姿都是用户输入，整单元锚定。
    let pinTranslation = false;
    let pinRotation = false;
    let hasDrivenMember = false;
    memberIds.forEach((memberId) => {
      if (!drivenIds.has(memberId)) {
        return;
      }
      hasDrivenMember = true;
      const constraint = input.drivenComponentConstraints?.[memberId];
      pinTranslation = pinTranslation || (constraint ? constraint.preserveTranslation !== false : true);
      pinRotation = pinRotation || (constraint ? constraint.preserveRotation !== false : true);
    });
    if (hasDrivenMember) {
      if (pinTranslation && pinRotation) {
        anchoredUnitIds.add(unitId);
      } else {
        partialFreedomByUnitId.set(unitId, { freeTranslation: !pinTranslation, freeRotation: !pinRotation });
      }
    }
  });
  const resolveUnitId = (componentId: string): string => (
    rigidGroups.groupRootByComponentId.get(componentId) ?? componentId
  );
  if (anchoredUnitIds.size === 0 && partialFreedomByUnitId.size > 0) {
    /**
     * 无硬锚的驱动场景（全浮动机构被驱动）：锚定"驱动件绕之旋转的枢轴基座"单元为参考。
     * 驱动件被绕某已提交同轴心旋转后，该铰链当前几何仍近似满足——取其另一端单元锚定，
     * 上半子装配随之保持原位，防止全局平移零空间让最小范数解把整个机构逐帧漂移
     * （驱动平面回环求解器时代 aaff730fb 的枢轴基座教训，移植为内核语义）。
     */
    let pivotUnitId: string | null = null;
    let pivotUnitSize = -1;
    for (const mate of participatingMates) {
      if (mate.mateType !== AssemblyMateType.Concentric || transientMateIds.has(mate.mateId)) {
        continue;
      }
      const firstUnitId = resolveUnitId(mate.firstEntity.componentInstanceId);
      const secondUnitId = resolveUnitId(mate.secondEntity.componentInstanceId);
      const firstIsDriven = partialFreedomByUnitId.has(firstUnitId);
      const secondIsDriven = partialFreedomByUnitId.has(secondUnitId);
      if (firstIsDriven === secondIsDriven) {
        continue;
      }
      const partnerUnitId = firstIsDriven ? secondUnitId : firstUnitId;
      const solvedMate = baseSolvedMates.get(mate.mateId)!;
      const firstAxis = resolveAxisGeometry(solvedMate.firstEntity);
      const secondAxis = resolveAxisGeometry(solvedMate.secondEntity);
      if (!firstAxis || !secondAxis) {
        continue;
      }
      // 铰链判据：两轴垂直距离近似为零（驱动旋转正绕该铰链进行；方向按无向中心线不苛求）。
      const offset = secondAxis.origin.clone().sub(firstAxis.origin);
      const direction = firstAxis.direction.clone().normalize();
      const radialDistance = offset.clone().sub(direction.multiplyScalar(offset.dot(firstAxis.direction))).length();
      if (radialDistance > tolerance.linear * 100) {
        continue;
      }
      const partnerSize = memberIdsByUnitId.get(partnerUnitId)?.length ?? 0;
      if (partnerSize > pivotUnitSize) {
        pivotUnitSize = partnerSize;
        pivotUnitId = partnerUnitId;
      }
    }
    if (pivotUnitId) {
      anchoredUnitIds.add(pivotUnitId);
    }
  }
  if (anchoredUnitIds.size === 0 && partialFreedomByUnitId.size === 0) {
    let largestUnitId: string | null = null;
    let largestSize = 0;
    memberIdsByUnitId.forEach((memberIds, unitId) => {
      if (memberIds.length > largestSize) {
        largestSize = memberIds.length;
        largestUnitId = unitId;
      }
    });
    if (largestUnitId) {
      anchoredUnitIds.add(largestUnitId);
    }
  }

  /**
   * 驱动连通分量裁剪：驱动场景的剩余自由度变量只包含"经配合图与驱动单元连通（不穿过锚定单元）"
   * 的浮动单元。断开子图（如另一处固定件挂着的孤立零件）即使残差为零也不进入本次驱动求解空间；
   * 其配合的满足性仍由整轮 validation 权威判定，不满足时内核候选被拒、回落旧行为。
   */
  const drivenReachableUnitIds = (() => {
    const drivenUnitIds = new Set<string>();
    memberIdsByUnitId.forEach((memberIds, unitId) => {
      if (memberIds.some((memberId) => drivenIds.has(memberId))) {
        drivenUnitIds.add(unitId);
      }
    });
    if (drivenUnitIds.size === 0) {
      return null;
    }
    const adjacency = new Map<string, Set<string>>();
    for (const mate of participatingMates) {
      const firstUnitId = resolveUnitId(mate.firstEntity.componentInstanceId);
      const secondUnitId = resolveUnitId(mate.secondEntity.componentInstanceId);
      if (firstUnitId === secondUnitId) {
        continue;
      }
      (adjacency.get(firstUnitId) ?? adjacency.set(firstUnitId, new Set()).get(firstUnitId)!).add(secondUnitId);
      (adjacency.get(secondUnitId) ?? adjacency.set(secondUnitId, new Set()).get(secondUnitId)!).add(firstUnitId);
    }
    // 从驱动单元（含完全锚定的驱动根）出发扩散；其它锚定单元是墙——不入可达集也不再向外扩散。
    const reachable = new Set<string>(drivenUnitIds);
    const queue = [...drivenUnitIds];
    while (queue.length > 0) {
      const currentUnitId = queue.shift()!;
      (adjacency.get(currentUnitId) ?? new Set<string>()).forEach((neighborUnitId) => {
        if (reachable.has(neighborUnitId) || anchoredUnitIds.has(neighborUnitId)) {
          return;
        }
        reachable.add(neighborUnitId);
        queue.push(neighborUnitId);
      });
    }
    return reachable;
  })();

  const variableUnits: NumericKernelUnit[] = [];
  memberIdsByUnitId.forEach((memberIds, unitId) => {
    if (anchoredUnitIds.has(unitId)) {
      return;
    }
    if (drivenReachableUnitIds && !drivenReachableUnitIds.has(unitId)) {
      return;
    }
    const partialFreedom = partialFreedomByUnitId.get(unitId);
    variableUnits.push({
      unitId,
      memberIds,
      referencePoint: resolveUnitReferencePoint(input.components, memberIds),
      freeTranslation: partialFreedom?.freeTranslation ?? true,
      freeRotation: partialFreedom?.freeRotation ?? true,
    });
  });
  if (variableUnits.length === 0 || variableUnits.length > MAX_VARIABLE_UNIT_COUNT) {
    appendStandardMateSolverDebugTraceEvent(debugTrace, 'globalNumericKernel.bail', {
      reason: variableUnits.length === 0 ? 'no-variable-unit' : 'too-many-units',
      variableUnitCount: variableUnits.length,
    });
    return null;
  }
  const unitIndexByUnitId = new Map<string, number>(variableUnits.map((unit, index) => [unit.unitId, index]));

  // 残差谱：跳过块内/冗余配合；双锚定或同单元的不满足配合意味着几何上不可解，直接 bail。
  const mateSpecs: NumericKernelMateSpec[] = [];
  for (const mate of participatingMates) {
    if (rigidGroups.internalMateIds.has(mate.mateId) || rigidGroups.redundantMateIds.has(mate.mateId)) {
      continue;
    }
    const firstUnitId = resolveUnitId(mate.firstEntity.componentInstanceId);
    const secondUnitId = resolveUnitId(mate.secondEntity.componentInstanceId);
    const spec: NumericKernelMateSpec = {
      mate,
      unitIds: [firstUnitId, secondUnitId],
      directionSign: resolveDirectionSign(mate, baseSolvedMates.get(mate.mateId)!),
      // 已提交同轴心轴向按无向中心线语义动态取半球，与 validation 的无向校验同源。
      dynamicUnsignedDirection: mate.mateType === AssemblyMateType.Concentric && !transientMateIds.has(mate.mateId),
      residualRowCount: resolveMateResidualRowCount(mate.mateType),
    };
    const isConstant = spec.unitIds.every((unitId) => !unitIndexByUnitId.has(unitId));
    const isSameUnit = firstUnitId === secondUnitId;
    if (isConstant || isSameUnit) {
      const constantResiduals: number[] = [];
      appendStandardMateKernelResiduals(constantResiduals, baseSolvedMates.get(mate.mateId)!, spec, directionScale);
      if (calculateStandardMateMaxResidual(constantResiduals) > tolerance.linear) {
        appendStandardMateSolverDebugTraceEvent(debugTrace, 'globalNumericKernel.bail', {
          reason: isSameUnit ? 'same-unit-violation' : 'anchored-violation',
          mateId: mate.mateId,
        });
        return null;
      }
      continue;
    }
    mateSpecs.push(spec);
  }
  if (mateSpecs.length === 0) {
    return null;
  }

  /**
   * 逐单元特征杠杆臂：该单元参与残差的实体几何点到旋转参考点的最大距离（初值位姿）。
   *
   * 用于参数空间度量重标定 ω' = L·ω：旋转分量的"1 单位"经杠杆臂放大后点位移可远大于
   * 等值平移，等权度量下 LM 下降路径会用"自转顶替平移"给出几何合法但违背最小点运动直觉的
   * 怪异解（历史解为纯平移的场景解出大角度自转）。把雅可比旋转列乘 1/L、解出后步长旋转
   * 分量再乘 1/L，即在"点位移"度量下做最速下降与最小范数正则，解回归最小点运动语义。
   */
  const rotationLeverArmByUnitIndex: number[] = variableUnits.map(() => 0);
  mateSpecs.forEach((spec) => {
    const solvedMate = baseSolvedMates.get(spec.mate.mateId)!;
    spec.unitIds.forEach((unitId, sideIndex) => {
      const unitIndex = unitIndexByUnitId.get(unitId);
      if (unitIndex === undefined) {
        return;
      }
      const entity = sideIndex === 0 ? solvedMate.firstEntity : solvedMate.secondEntity;
      const point = resolveAxisGeometry(entity)?.origin ?? resolvePlaneGeometry(entity)?.point ?? null;
      if (!point) {
        return;
      }
      const leverArm = point.clone().sub(variableUnits[unitIndex]!.referencePoint).length();
      rotationLeverArmByUnitIndex[unitIndex] = Math.max(rotationLeverArmByUnitIndex[unitIndex]!, leverArm);
    });
  });
  const parameterScales: number[] = [];
  variableUnits.forEach((_, unitIndex) => {
    // 杠杆臂下限 1：旋转"不比平移便宜"是重标定的底线；实体贴近参考点时保持 1:1 量纲。
    const rotationScale = 1 / Math.max(rotationLeverArmByUnitIndex[unitIndex]!, 1);
    parameterScales.push(1, 1, 1, rotationScale, rotationScale, rotationScale);
  });

  /**
   * 按给定残差谱执行一次 on-manifold LM 求解并构建经 validation 的结果。
   *
   * 数值方法：每次迭代恒在 δ=0 处解析线性化（δp = δv + δω×(p−ref)、δd = δω×d），
   * 已接受步长立即合入当前位姿与累计 delta——不在单一参数图上累积大角度，规避
   * 旋转向量图的远域畸变，也让解析雅可比恒为精确一阶模型。
   *
   * @param specs 残差谱（半球首选分支或瞬态翻转分支）。
   * @param initialUnitDeltas 各单元初始世界 delta（瞬态翻转重试的预翻初值）；缺省单位阵。
   * @returns 通过 validation 的结果；未收敛/校验未过时返回 null。
   */
  const attemptSolve = (
    specs: readonly NumericKernelMateSpec[],
    initialUnitDeltas?: ReadonlyMap<number, THREE.Matrix4>,
    translationOnly = false,
  ): StandardMateSolverResult | null => {
    const rowOffsets: number[] = [];
    let totalRows = 0;
    specs.forEach((spec) => {
      rowOffsets.push(totalRows);
      totalRows += spec.residualRowCount;
    });

    // 单元运行时状态：累计 delta + 随动参考点；当前组件状态表按初始 delta 建立。
    const unitStates: NumericKernelUnitState[] = variableUnits.map((unit, unitIndex) => {
      const initialDelta = initialUnitDeltas?.get(unitIndex) ?? new THREE.Matrix4().identity();
      return {
        accumulatedDelta: initialDelta.clone(),
        referencePoint: unit.referencePoint.clone().applyMatrix4(initialDelta),
      };
    });
    let currentComponents: Record<string, AssemblyMateSolverComponentState> = { ...input.components };
    variableUnits.forEach((unit, unitIndex) => {
      applyUnitDeltaToComponents(currentComponents, unit, unitStates[unitIndex]!.accumulatedDelta, input.components);
    });

    const resolveKinematics = (unitId: string): KernelEntityKinematics => {
      const unitIndex = unitIndexByUnitId.get(unitId);
      return unitIndex === undefined
        ? { unitIndex: null, referencePoint: null }
        : { unitIndex, referencePoint: unitStates[unitIndex]!.referencePoint };
    };
    const evaluateState = (
      components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
    ): { residuals: number[]; solvedMates: AssemblyMateDefinition[] } => {
      const residuals: number[] = [];
      const solvedMates: AssemblyMateDefinition[] = [];
      specs.forEach((spec) => {
        const solvedMate = transformMateDefinitionForSolve(spec.mate, baseDeltaMap, components);
        solvedMates.push(solvedMate);
        appendStandardMateKernelResiduals(residuals, solvedMate, spec, directionScale);
      });
      return { residuals, solvedMates };
    };

    appendStandardMateSolverDebugTraceEvent(debugTrace, 'globalNumericKernel.begin', {
      variableUnits: variableUnits.map((unit) => ({
        unitId: unit.unitId,
        members: unit.memberIds.length,
        freeTranslation: unit.freeTranslation,
        freeRotation: unit.freeRotation,
      })),
      anchoredUnitIds: [...anchoredUnitIds],
      mateCount: specs.length,
      residualRows: totalRows,
    });

    const parameterCount = variableUnits.length * 6;
    let { residuals, solvedMates } = evaluateState(currentComponents);
    let damping = 1e-6;
    let converged = calculateStandardMateMaxResidual(residuals) <= tolerance.linear;

    // 偏锚定单元的钉死参数列：驱动件被保留的位姿分量不进变量（列清零后法方程给出 δ=0）。
    // translationOnly 阶段额外钉死全部旋转参数——见主流程"两段式运动类型偏好"说明。
    const pinnedParameterIndices: number[] = [];
    variableUnits.forEach((unit, unitIndex) => {
      if (!unit.freeTranslation) {
        pinnedParameterIndices.push(unitIndex * 6, unitIndex * 6 + 1, unitIndex * 6 + 2);
      }
      if (!unit.freeRotation || translationOnly) {
        pinnedParameterIndices.push(unitIndex * 6 + 3, unitIndex * 6 + 4, unitIndex * 6 + 5);
      }
    });

    for (let iteration = 0; iteration < MAX_ITERATION_COUNT && !converged; iteration += 1) {
      // 解析雅可比：δ=0 处闭式一阶模型（重线性化保证恒精确）。
      const jacobian: number[][] = Array.from({ length: totalRows }, () => Array(parameterCount).fill(0));
      specs.forEach((spec, specIndex) => {
        appendStandardMateKernelJacobianRows(
          jacobian,
          rowOffsets[specIndex]!,
          solvedMates[specIndex]!,
          spec,
          resolveKinematics(spec.unitIds[0]!),
          resolveKinematics(spec.unitIds[1]!),
          directionScale,
        );
      });
      pinnedParameterIndices.forEach((parameterIndex) => {
        for (let rowIndex = 0; rowIndex < totalRows; rowIndex += 1) {
          jacobian[rowIndex]![parameterIndex] = 0;
        }
      });
      // 参数度量重标定：旋转列乘 1/L（见 parameterScales 说明），解出后步长旋转分量再乘 1/L 还原。
      for (let parameterIndex = 0; parameterIndex < parameterCount; parameterIndex += 1) {
        const scale = parameterScales[parameterIndex] ?? 1;
        if (scale !== 1) {
          for (let rowIndex = 0; rowIndex < totalRows; rowIndex += 1) {
            jacobian[rowIndex]![parameterIndex] *= scale;
          }
        }
      }

      const scaledStepVector = solveStandardMateDampedLeastSquaresStep(jacobian, residuals, damping);
      if (!scaledStepVector) {
        damping *= 10;
        continue;
      }
      const stepVector = scaledStepVector.map((value, parameterIndex) => value * (parameterScales[parameterIndex] ?? 1));
      // 数值兜底：钉死分量强制归零（列清零后理论上已为 0，此处防线性求解的数值噪声渗入）。
      pinnedParameterIndices.forEach((parameterIndex) => {
        stepVector[parameterIndex] = 0;
      });
      // 试算：把步长按各单元世界 delta 应用到当前位姿。
      const trialDeltas = variableUnits.map((_, unitIndex) => (
        composeUnitWorldDelta(stepVector, unitIndex * 6, unitStates[unitIndex]!.referencePoint)
      ));
      const trialComponents: Record<string, AssemblyMateSolverComponentState> = { ...currentComponents };
      variableUnits.forEach((unit, unitIndex) => {
        applyUnitDeltaToComponents(trialComponents, unit, trialDeltas[unitIndex]!, currentComponents);
      });
      const trialEvaluation = evaluateState(trialComponents);
      const currentCost = calculateStandardMateResidualCost(residuals);
      const nextCost = calculateStandardMateResidualCost(trialEvaluation.residuals);
      appendStandardMateSolverDebugTraceEvent(debugTrace, 'globalNumericKernel.iteration', {
        iteration,
        damping,
        currentCost,
        nextCost,
        accepted: nextCost < currentCost,
        stepNorm: Math.hypot(...stepVector),
        maxResidual: calculateStandardMateMaxResidual(residuals),
      });
      if (nextCost < currentCost) {
        // 接受：合入当前位姿并推进累计 delta 与参考点（on-manifold 重线性化）。
        currentComponents = trialComponents;
        residuals = trialEvaluation.residuals;
        solvedMates = trialEvaluation.solvedMates;
        variableUnits.forEach((_, unitIndex) => {
          const state = unitStates[unitIndex]!;
          state.accumulatedDelta = trialDeltas[unitIndex]!.clone().multiply(state.accumulatedDelta);
          state.referencePoint = state.referencePoint.clone().applyMatrix4(trialDeltas[unitIndex]!);
        });
        damping = Math.max(damping * 0.2, 1e-12);
        converged = calculateStandardMateMaxResidual(residuals) <= tolerance.linear;
      } else {
        damping *= 10;
      }
    }

    if (!converged) {
      // 最大残差归因：收敛末态残差最大的分量所属配合即最难满足的约束，随 trace/采集输出供排障定位。
      let worstRowIndex = 0;
      residuals.forEach((value, rowIndex) => {
        if (Math.abs(value) > Math.abs(residuals[worstRowIndex] ?? 0)) {
          worstRowIndex = rowIndex;
        }
      });
      let worstSpecIndex = -1;
      for (let specIndex = rowOffsets.length - 1; specIndex >= 0; specIndex -= 1) {
        if (rowOffsets[specIndex]! <= worstRowIndex) {
          worstSpecIndex = specIndex;
          break;
        }
      }
      appendStandardMateSolverDebugTraceEvent(debugTrace, 'globalNumericKernel.unconverged', {
        maxResidual: calculateStandardMateMaxResidual(residuals),
        worstMateId: worstSpecIndex >= 0 ? specs[worstSpecIndex]!.mate.mateId : null,
        elapsedMs: performance.now() - startedAt,
      });
      return null;
    }

    // 收敛：按累计 delta 对原始输入做单次刚体写回（scale 不进计算），交整轮 validation 权威判定。
    const mutableComponents: Record<string, AssemblyMateSolverComponentState> = { ...input.components };
    const componentTransforms: Record<string, AssemblyMateSolverComponentState['transform']> = {};
    Object.values(input.components).forEach((component) => {
      componentTransforms[component.componentInstanceId] = component.transform;
    });
    const finalDeltaMap = createComponentWorldDeltaMap(input.components);
    variableUnits.forEach((unit, unitIndex) => {
      const delta = unitStates[unitIndex]!.accumulatedDelta;
      unit.memberIds.forEach((memberId) => {
        const component = mutableComponents[memberId];
        if (!component) {
          return;
        }
        const transformed = transformComponentStateByWorldDelta(component, delta);
        mutableComponents[memberId] = transformed;
        componentTransforms[memberId] = transformed.transform;
        finalDeltaMap[memberId] = delta.clone();
      });
    });

    const result = createGraphSolverResult(input, componentTransforms, mutableComponents, finalDeltaMap);
    appendStandardMateSolverDebugTraceEvent(debugTrace, 'globalNumericKernel.result', {
      status: result.status,
      maxResidual: calculateStandardMateMaxResidual(residuals),
      elapsedMs: performance.now() - startedAt,
      diagnostics: result.diagnostics.filter((diagnostic) => diagnostic.status !== MateSolveStatus.Solved),
    });
    return result.status === MateSolveStatus.Solved ? result : null;
  };

  /**
   * 两段式运动类型偏好：初值方向残差已满足（轴/法向已对齐）时，先尝试**纯平移**求解——
   * 与历史投影行为同语义："能平移闭合就不让跟随件无故自转"。旋转与平移目标存在耦合投影时，
   * 任何有限度量下的最小范数解都会让旋转"顺路"参与，产生几何合法但违背直觉的自转分摊，
   * 只有显式钉死旋转才能复刻纯平移语义。方向未对齐或纯平移不收敛时回落全参数求解。
   */
  const initialDirectionSatisfied = mateSpecs.every((spec) => {
    const rows: number[] = [];
    appendStandardMateKernelResiduals(rows, baseSolvedMates.get(spec.mate.mateId)!, spec, directionScale);
    const directionRowCount = spec.mate.mateType === AssemblyMateType.Perpendicular ? 1 : 4;
    return rows.slice(0, directionRowCount).every((value) => Math.abs(value) <= tolerance.linear);
  });
  const preferredResult = (initialDirectionSatisfied ? attemptSolve(mateSpecs, undefined, true) : null)
    ?? attemptSolve(mateSpecs);
  if (preferredResult) {
    return preferredResult;
  }

  // 半球分支失败：对瞬态方向配合翻转分支重试一次，覆盖"用户刚切换对齐、几何仍在旧分支"的场景。
  const hasFlippableTransient = mateSpecs.some((spec) => (
    transientMateIds.has(spec.mate.mateId) && spec.mate.mateType !== AssemblyMateType.Perpendicular
  ));
  if (!hasFlippableTransient) {
    return null;
  }
  appendStandardMateSolverDebugTraceEvent(debugTrace, 'globalNumericKernel.retryFlippedTransient', {});
  const flippedSpecs = mateSpecs.map((spec) => (
    transientMateIds.has(spec.mate.mateId) && spec.mate.mateType !== AssemblyMateType.Perpendicular
      ? { ...spec, directionSign: -spec.directionSign }
      : spec
  ));
  /**
   * 预翻初值：翻转分支的目标方向与初值精确反平行时，cross 与鞍点脱困项在 θ=π 处梯度双双为零，
   * LM 无法自行离开鞍点。按该场景的物理语义（绕过枢轴点、垂直于法向的轴整翻 180°，圆孔同轴心的
   * 无向中心线仍重合），把承载翻转瞬态的变量单元**连同其经已提交配合连通的全部变量单元**当作
   * 一个刚体绕公共枢轴预翻 π——只翻承载单元会把它与跟随子件的已提交配合推入新的反平行鞍点。
   */
  const initialUnitDeltas = new Map<number, THREE.Matrix4>();
  const carrierSpec = flippedSpecs.find((spec) => (
    transientMateIds.has(spec.mate.mateId) && spec.mate.mateType !== AssemblyMateType.Perpendicular
  ));
  const carrierUnitIndex = carrierSpec?.unitIds
    .map((unitId) => unitIndexByUnitId.get(unitId))
    .find((unitIndex): unitIndex is number => unitIndex !== undefined);
  const flipDirection = carrierSpec
    ? resolveEntityDirection(baseSolvedMates.get(carrierSpec.mate.mateId)!, 'first')
    : null;
  if (carrierSpec && carrierUnitIndex !== undefined && flipDirection) {
    // 枢轴点：瞬态变量侧实体的轴原点/面点（基态），对应"绕过轴心原点的垂直轴"语义。
    const carrierSolvedMate = baseSolvedMates.get(carrierSpec.mate.mateId)!;
    const carrierVariableSide = unitIndexByUnitId.has(resolveUnitId(carrierSpec.mate.secondEntity.componentInstanceId))
      ? 'second'
      : 'first';
    const carrierEntity = carrierVariableSide === 'second' ? carrierSolvedMate.secondEntity : carrierSolvedMate.firstEntity;
    // 枢轴优先取与瞬态同组件对的已提交同轴心轴上点：绕轴上点垂直轴整翻 π 后，
    // 该同轴心的无向中心线仍精确重合（方向反向、位置共线），翻转不破坏既有圆柱副。
    const carrierPairKey = [carrierSpec.mate.firstEntity.componentInstanceId, carrierSpec.mate.secondEntity.componentInstanceId]
      .sort()
      .join('|');
    const pairConcentricPivot = participatingMates
      .filter((mate) => (
        !transientMateIds.has(mate.mateId)
        && mate.mateType === AssemblyMateType.Concentric
        && [mate.firstEntity.componentInstanceId, mate.secondEntity.componentInstanceId].sort().join('|') === carrierPairKey
      ))
      .map((mate) => resolveAxisGeometry(baseSolvedMates.get(mate.mateId)!.firstEntity)?.origin ?? null)
      .find((origin): origin is THREE.Vector3 => origin !== null) ?? null;
    const pivotPoint = pairConcentricPivot
      ?? resolveAxisGeometry(carrierEntity)?.origin
      ?? resolvePlaneGeometry(carrierEntity)?.point
      ?? variableUnits[carrierUnitIndex]!.referencePoint;
    const helper = Math.abs(flipDirection.x) < 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
    const perpendicularAxis = flipDirection.clone().cross(helper).normalize();

    // BFS：从承载单元出发，沿"已提交配合连接的变量单元"扩展，锚定单元天然不在变量表内。
    const adjacentUnitIndices = new Map<number, Set<number>>();
    flippedSpecs.forEach((spec) => {
      if (transientMateIds.has(spec.mate.mateId)) {
        return;
      }
      const indices = spec.unitIds
        .map((unitId) => unitIndexByUnitId.get(unitId))
        .filter((unitIndex): unitIndex is number => unitIndex !== undefined);
      if (indices.length === 2 && indices[0] !== indices[1]) {
        (adjacentUnitIndices.get(indices[0]!) ?? adjacentUnitIndices.set(indices[0]!, new Set()).get(indices[0]!)!).add(indices[1]!);
        (adjacentUnitIndices.get(indices[1]!) ?? adjacentUnitIndices.set(indices[1]!, new Set()).get(indices[1]!)!).add(indices[0]!);
      }
    });
    const flipUnitIndices = new Set<number>([carrierUnitIndex]);
    const queue = [carrierUnitIndex];
    while (queue.length > 0) {
      const currentIndex = queue.shift()!;
      (adjacentUnitIndices.get(currentIndex) ?? new Set<number>()).forEach((neighborIndex) => {
        if (!flipUnitIndices.has(neighborIndex)) {
          flipUnitIndices.add(neighborIndex);
          queue.push(neighborIndex);
        }
      });
    }

    // 刚体整翻：连通变量子图共享同一 delta M = T(pivot)∘R(π)∘T(−pivot)，作为初始 delta 直接下发。
    const flipDelta = new THREE.Matrix4()
      .makeTranslation(pivotPoint.x, pivotPoint.y, pivotPoint.z)
      .multiply(new THREE.Matrix4().makeRotationAxis(perpendicularAxis, Math.PI))
      .multiply(new THREE.Matrix4().makeTranslation(-pivotPoint.x, -pivotPoint.y, -pivotPoint.z));
    flipUnitIndices.forEach((unitIndex) => {
      const unit = variableUnits[unitIndex]!;
      // 偏锚定单元（驱动件部分保留位姿）不参与预翻：整翻会破坏被保留的分量。
      if (unit.freeTranslation && unit.freeRotation) {
        initialUnitDeltas.set(unitIndex, flipDelta.clone());
      }
    });
  }
  return attemptSolve(flippedSpecs, initialUnitDeltas);
}
